import { DateTime, Duration } from 'luxon';

import { TextTimeSeriesState } from '../types';

const mergeTemplateAndData = (state: TextTimeSeriesState): TextTimeSeriesState => {
    const { datasets, viewblockState, time } = state;
    const { settings, databilities } = viewblockState;

    if (!datasets?.length || !databilities.length || !time) {
        return state;
    }

    const datasetIds = databilities.map(({ datasetId }) => datasetId);
    const capabilityIds = databilities.map(({ capabilityId }) => capabilityId);
    const filteredDatasets = datasets.filter(
        (dataset) => !dataset.error && !!dataset.datasetId && datasetIds.includes(dataset.datasetId) && capabilityIds.includes(dataset.capabilityId!)
    );

    if (filteredDatasets.length === 0) {
        return state;
    }

    const now = DateTime.fromISO(time.iso8601).setZone(time.timezone);
    const [datetimeFrom, datetimeTo] = viewblockState.settings.timeRange.split('--').map((durationISOString) => now.plus(Duration.fromISO(durationISOString)));
    const fromTimeIndex = (filteredDatasets[0].cfjson!.variables.time.data as string[]).findIndex((rowDateTime) => {
        return DateTime.fromISO(rowDateTime).valueOf() >= datetimeFrom.startOf('day').valueOf();
    });

    const toTimeIndex = (filteredDatasets[0].cfjson!.variables.time.data as string[]).findIndex((rowDateTime) => {
        return DateTime.fromISO(rowDateTime).valueOf() >= datetimeTo.startOf('day').valueOf();
    });

    const rows = filteredDatasets.map((filteredDataset) => {
        if (!filteredDataset.cfjson || !filteredDataset.capability) return { label: '', rowValues: [] };
        const variable = Object.keys(filteredDataset.capability.variables)[0];
        const cfjsonVariable = filteredDataset.cfjson.variables[variable]
            ? filteredDataset.cfjson.variables[variable]
            : filteredDataset.cfjson.variables[variable.split('_')[1]];

        if (!cfjsonVariable) return { label: '', rowValues: [] };
        const datums = cfjsonVariable.data.slice(fromTimeIndex, toTimeIndex) as string[];
        const databilityIndex = databilities.findIndex(({ id }) => id === filteredDataset.databilityId);

        return {
            label: settings.labelDatabilities[databilityIndex] || filteredDataset.capability.display_name_short,
            rowValues: datums,
        };
    });

    const columns = (filteredDatasets[0].cfjson!.variables.time.data as string[])
        .slice(fromTimeIndex, toTimeIndex)
        .map((isoDate) => DateTime.fromISO(isoDate).setZone(time.timezone).setLocale(settings.timeLocale));
    const offsetLeft = !!state.viewblockState.settings.useFixedOffset && typeof settings.offsetLeft !== 'undefined' ? settings.offsetLeft : undefined;

    return {
        ...state,
        model: {
            timezone: time.timezone,
            fontSize: state.viewblockState.settings.fontSize,
            columns,
            offsetLeft,
            rows,
            displayTableHeading: state.viewblockState.settings.displayTableHeading,
        },
    };
};

export default mergeTemplateAndData;
